// Mount the on-device splat MESH: the lift writes three's buffers directly, with
// NO readback, and the app's own renderer draws them in the app's own scene.
//
// IT USED TO MOUNT A CANVAS. One device (`sharedGpuDevice.js`) removed the reason
// there were two — see the header of `three/ThreeSplatBridge.js` — so what is
// left here is wiring, not compositing.
//
// WHY THIS IS SHARED. Both render paths want it and the wiring is identical —
// the splats' placement matrix, the region list, the mesh that holds them. It lived inline in `OGSModel.jsx`, so the next path had no way
// to reach it without copying ~125 lines, and copied wiring is how the two paths
// would drift on the one thing that decides whether a frame stalls on `mapAsync`.
//
// THREE IS THE ONLY RENDERER HERE. The hand-written WebGPU splat renderer that
// used to own a unified splat buffer, a region table and its own depth sort is
// gone: three's `GaussianSplat` draws the same regions from buffers the lift
// writes DIRECTLY (`setThreeTargets`), so the unified buffer, the per-frame
// layout conversion and a second sort implementation had no job left.
//
// It lives under `src/ogs/render/` rather than `src/ogs/pipeline/` because
// docs/OGS_ARCHITECTURE.md §6 forbids the legacy components importing the next
// path — legacy has to stay deletable — and this is the shared ground both
// already import. It touches no DOM and no React, so it is callable from either
// side.
//
// WHAT "external" MEANS FOR THE CALLER. With an overlay mounted, the renderer
// node takes `present: 'external'`: a frame is the lift SUBMITTING and nothing
// else. Without one the PACKED present runs — `SplatMeshUpdater`'s own mesh,
// fed by a map and a CPU copy every presented frame. So the return value of this
// function IS the answer to "does this path read back per frame". (The packed
// present used to be Spark's and several messages here still said so; it is
// three's `GaussianSplat` on both sides now, differing only in who fills it.)
import * as THREE from 'three'
import { requestSharedGpuDevice } from '../../lib/sharedGpuDevice.js'
import { checkRenderContract } from './renderContract.js'

/** Storage buffers the heaviest pass on this path binds. It is WebGPU's DEFAULT
 *  limit, so no adapter is refused the overlay over it — the old requirement of
 *  10 belonged to the deleted renderer's depth sort, and turned away devices
 *  reporting exactly 8 that could run this path perfectly well. The lift binds 8
 *  (see liftStorageBuffers.test.mjs) and the stable sort binds 8. */
const OVERLAY_STORAGE_BUFFERS = 8

/**
 * The DEVICE half of the question, answerable at boot — before a bundle, a
 * branch or an environment has been fetched.
 *
 * It is split out because the environment has to decide WHICH MESH IT JOINS
 * before it starts loading: a `GaussianSplat` sorts only its own splats, so the
 * avatar and the room must share one mesh or the room paints over the face — and
 * deciding after the file is parsed means either a converter or a second parse.
 *
 * `navigator.gpu` plus a limits check is genuinely all the device half is; the
 * branch half below is what needs the pipeline.
 *
 * @returns {Promise<string|null>} the REASON it cannot, or null
 */
export async function overlayDeviceRefusal() {
  if (typeof navigator === 'undefined' || !navigator.gpu) {
    return 'this browser has no WebGPU'
  }
  try {
    const device = await requestSharedGpuDevice()
    // The page's one device is what the renderer AND the lift run on, so it is
    // the only one worth asking about. Falling back to a fresh adapter here
    // would answer for a device nobody uses.
    const limit = device
      ? (device.limits?.maxStorageBuffersPerShaderStage ?? 0)
      : ((await navigator.gpu.requestAdapter())?.limits?.maxStorageBuffersPerShaderStage ?? 0)
    if (!limit) return 'no WebGPU device'
    if (limit < OVERLAY_STORAGE_BUFFERS) {
      return `maxStorageBuffersPerShaderStage=${limit} < ${OVERLAY_STORAGE_BUFFERS} needed by the lift and sort`
    }
  } catch (e) {
    return `WebGPU device request failed: ${e?.message ?? e}`
  }
  return null
}

/** Can this device and branch set take the overlay? The REASON when not, so the
 *  caller can report the packed fallback rather than falling back silently.
 *
 *  The BRANCH half cannot be hoisted: whether a lifter writes into a device
 *  buffer is only known once the pipeline has built one. So this stays the final
 *  word, and a late refusal here must hand the environment back rather than leave
 *  it drawn by nobody — see `environmentOwner`. */
export function overlayRefusal(device, branches) {
  if (!device) return 'the on-device splat overlay needs the GPU lift (no WebGPU device, or ?gpulift=0)'
  if (!branches.some((br) => br.lifter?.renderSplatsBuffer)) {
    return 'no branch lifts into a device buffer the renderer can draw'
  }
  const limit = device.limits?.maxStorageBuffersPerShaderStage ?? 0
  if (limit < OVERLAY_STORAGE_BUFFERS) {
    return `device maxStorageBuffersPerShaderStage=${limit} < ${OVERLAY_STORAGE_BUFFERS} needed by the lift and sort`
  }
  return null
}

/**
 * @returns {Promise<object|null>} null when the caller must keep the PACKED
 *   present — `overlayRefusal` says why.
 */
export async function mountSplatOverlay({
  device, branches, camera, renderer, scene, avatarRoot,
  worldScale = 1, worldOffset = [0, 0, 0], worldRotation = null,
  isCancelled = () => false,
  log = console,
}) {
  // NOT A REFUSAL, A MISCALL. `renderer` and `scene` replaced the `glCanvas`
  // this used to take, and a caller still passing the old argument would build a
  // mesh against `undefined` — which throws inside the try below, gets reported
  // as a failure and falls back to the PACKED present with a readback per frame.
  // That is slow rather than broken, so it survives a smoke test; the caller's
  // `pace` awaiting a `nextFrame()` nobody resolves is what actually stalls. It
  // is a programming error either way, so it says so.
  if (!renderer || !scene) {
    throw new Error('mountSplatOverlay: needs the app `renderer` and `scene` — '
      + "the splats are drawn in the app's own scene, not on a canvas of their own")
  }
  const refusal = overlayRefusal(device, branches)
  if (refusal) { log.error?.(`[ogs] ${refusal} — using the packed present`); return null }

  const gpuBranches = branches.filter((br) => br.lifter?.renderSplatsBuffer)

  // The splats' frame: the SplatMesh's placement (cm→m scale, world_rotation,
  // world_offset) under the avatar root — the same transform SplatMeshUpdater
  // gives its own mesh, read per frame so a moving root carries the overlay.
  const placement = new THREE.Matrix4().compose(
    new THREE.Vector3(worldOffset[0], worldOffset[1], worldOffset[2]),
    worldRotation ?? new THREE.Quaternion(),
    new THREE.Vector3(worldScale, worldScale, worldScale),
  )
  const modelScratch = new THREE.Matrix4()
  const model = () => modelScratch.multiplyMatrices(avatarRoot.matrixWorld, placement)

  // ONE entry per region the lift emits. This used to be built for our own
  // renderer, which owned a unified splat buffer and a region table and handed
  // three what it needed; that renderer is gone, so the bookkeeping is just a
  // list and the bridge takes it directly.
  const sources = gpuBranches.map((br) => ({
    name: br.name,
    count: br.lifter.renderCountBuffer,
    capacity: br.lifter.renderCapacity,
    // The lift writes three's FOUR buffers itself (centre / covA / covB /
    // colour), so no conversion pass runs per frame. A lifter that refuses is a
    // hard failure rather than a fallback: the pass that used to cover it read
    // from a unified buffer nothing fills any more.
    redirectThree: (t) => br.lifter.setThreeTargets?.(t) ?? false,
    setModel: (elements) => br.lifter.setThreeModel?.(elements),
    // How many times this branch's splats have been rewritten. The driver sums
    // these to decide whether the order it holds still belongs to the splats it
    // is about to draw — see `ThreeSplatBridge.beginAppFrame`.
    generation: () => br.lifter.liftGeneration ?? 0,
  }))

  let bridge = null
  try {
    const { ThreeSplatBridge } = await import('./three/ThreeSplatBridge.js')
    bridge = await new ThreeSplatBridge({ device, sources, renderer, scene, camera, model }).init()
    if (isCancelled()) { bridge.dispose(); return null }
    bridge.start()
    window.__THREE_SPLAT__ = bridge
  } catch (e) {
    // A failure HERE is not the packed fallback: the mesh is half-built and the
    // updater's own was never filled, so nothing draws. The caller gets null and
    // reports it rather than showing an empty viewport.
    log.error?.('[ogs] the in-scene splat mesh failed; the splats are not drawn:', e?.message ?? e)
    try { bridge?.dispose() } catch { /* already gone */ }
    return null
  }

  log.info?.("[ogs] splats in the app's scene, no readback: "
    + gpuBranches.map((b) => b.name).join(' + '))
  // The mesh-side half of the contract, from the ground both paths call: the
  // class settings, a stable order, and a renderer that can tell a lift
  // happened. Each of those was wired in ONE path's component once, and the
  // other path silently went without — see `renderContract.js`.
  checkRenderContract({
    GaussianSplat: bridge.deps?.GaussianSplat ?? null,
    mesh: bridge.splat ?? null,
    order: bridge.stableSort ?? null,
    driver: bridge,
  }, { log, source: 'splat-mount' })

  return {
    renderer: bridge,
    bridge,
    dispose() {
      try { bridge.dispose() } catch { /* already gone */ }
      if (window.__THREE_SPLAT__ === bridge) delete window.__THREE_SPLAT__
    },
  }
}
